import React, { useState, useEffect } from "react";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  Stack,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Styles
import "../OpdStepper.css";

export const MIN_ITEMS = 1;
export const MAX_ITEMS = 3;
export const MAX_ITEMS_WITH_OTHERS = 4;
export const OTHER_SYMPTOM_ID = 370;

export const FIELDS = {
  complaints: "chiefComplaints",
  history: "briefHistory",
  otherSymptoms: "other_symptoms",
};

export const isOtherComplaint = (complaint) => {
  if (!complaint) return false;
  const id = typeof complaint === "object" ? (complaint.symptomId ?? complaint.symptom_id) : complaint;
  return Number(id) === OTHER_SYMPTOM_ID;
};

export const hasOthersSelected = (complaintsList = []) => {
  return Array.isArray(complaintsList) && complaintsList.some(isOtherComplaint);
};

export const getMaxAllowedComplaints = (complaintsList = []) => {
  return hasOthersSelected(complaintsList) ? MAX_ITEMS_WITH_OTHERS : MAX_ITEMS;
};

export const chiefComplaintsInitialValues = {
  [FIELDS.complaints]: [],
  [FIELDS.history]: [""],
  [FIELDS.otherSymptoms]: "",
};

export const chiefComplaintsValidationSchema = Yup.object({
  [FIELDS.complaints]: Yup.array()
    .of(Yup.object())
    .min(MIN_ITEMS, `Please select at least ${MIN_ITEMS} chief complaint`)
    .test(
      "max-complaints-limit",
      "Maximum chief complaints exceeded",
      function (value) {
        if (!value || !Array.isArray(value)) return true;
        const max = getMaxAllowedComplaints(value);
        const hasOthers = hasOthersSelected(value);
        if (value.length > max) {
          return this.createError({
            message: `You can select a maximum of ${max} chief complaints${hasOthers ? " (including Others)" : ""}`,
          });
        }
        return true;
      }
    )
    .required(`Please select at least ${MIN_ITEMS} chief complaint`),

  [FIELDS.history]: Yup.array()
    .of(
      Yup.string()
        .trim()
        .min(3, "Please enter at least 3 characters")
        .max(250, "Maximum 250 characters allowed")
        .required("History point cannot be empty")
    )
    .min(MIN_ITEMS, `Please add at least ${MIN_ITEMS} history point`)
    .max(MAX_ITEMS, `You can add a maximum of ${MAX_ITEMS} history points`),

  [FIELDS.otherSymptoms]: Yup.string().when(FIELDS.complaints, {
    is: (complaints) => hasOthersSelected(complaints),
    then: (schema) => schema.trim().required("Please specify other symptoms"),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

export default function Step2ChiefComplaints({
  nextStep,
  prevStep,
  initialValues,
  submitLabel = "Next",
  showActions = true,
}) {
  const [expanded, setExpanded] = useState({
    complaints: true,
    history: true,
  });

  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [fetchedData, setFetchedData] = useState(null);
  const [selectedCount, setSelectedCount] = useState(
    (initialValues?.[FIELDS.complaints] || []).length
  );
  const [hasOtherSelected, setHasOtherSelected] = useState(
    hasOthersSelected(initialValues?.[FIELDS.complaints] || [])
  );

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  // Load existing step 2 data if resuming visit
  useEffect(() => {
    let active = true;
    const displayId = localStorage.getItem("currentOpdDisplayId") || "";
    if (displayId) {
      const fetchStep2Data = async () => {
        try {
          const res = await eopdAPI.getOpdStepData(displayId, 2);
          if (active && res && res.success && res.data) {
            const complaints = Array.isArray(res.data.chief_complaints)
              ? res.data.chief_complaints.map((c) => ({
                symptomId: c.symptom_id ?? c.symptomId,
                symptomCode: c.symptom_code ?? c.symptomCode ?? "",
                displayName: c.symptom_name ?? c.displayName ?? "",
                categoryName: c.category_name ?? c.categoryName ?? "",
              }))
              : [];
            const history = Array.isArray(res.data.history) ? res.data.history : [];
            setSelectedCount(complaints.length);
            setHasOtherSelected(hasOthersSelected(complaints));
            setFetchedData({
              [FIELDS.complaints]: complaints,
              [FIELDS.otherSymptoms]: res.data.other_symptoms || res.data.other_symptom || "",
              [FIELDS.history]: history,
            });
          }
        } catch (err) {
          console.error("Error loading step 2 data:", err);
        }
      };
      fetchStep2Data();
    }
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (selectedCount >= MAX_ITEMS_WITH_OTHERS) {
      setOptions([]);
      return;
    }

    if (inputValue.trim().length < 3) {
      setOptions([]);
      return;
    }

    let active = true;
    const fetchSuggestions = async () => {
      setLoadingOptions(true);
      try {
        const displayId = localStorage.getItem("currentOpdDisplayId") || "";
        const response = await eopdAPI.getComplaintsAutocomplete(inputValue, displayId);
        if (active && response && response.success && Array.isArray(response.groups)) {
          const flattened = [];
          response.groups.forEach((group) => {
            if (Array.isArray(group.symptoms)) {
              group.symptoms.forEach((symptom) => {
                flattened.push({
                  symptomId: symptom.symptomId,
                  symptomCode: symptom.symptomCode,
                  displayName: symptom.displayName,
                  categoryName: group.categoryName,
                });
              });
            }
          });
          setOptions(flattened);
        }
      } catch (err) {
        console.error("Autocomplete fetch error:", err);
      } finally {
        if (active) {
          setLoadingOptions(false);
        }
      }
    };

    const timer = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, selectedCount, hasOtherSelected]);

  return (
    <Formik
      initialValues={{ ...chiefComplaintsInitialValues, ...(initialValues || {}), ...(fetchedData || {}) }}
      validationSchema={chiefComplaintsValidationSchema}
      enableReinitialize
      onSubmit={async (values, helpers) => {
        try {
          const displayId = localStorage.getItem("currentOpdDisplayId") || "";
          const hasOthers = hasOthersSelected(values[FIELDS.complaints]);
          const payload = {
            display_id: displayId,
            symptoms: (values[FIELDS.complaints] || []).map((c) =>
              Number(typeof c === "object" ? (c.symptomId ?? c.symptom_id) : c)
            ),
            other_symptoms: hasOthers ? values[FIELDS.otherSymptoms] || "" : "",
            history: (values[FIELDS.history] || []).map((v) => v.trim()).filter(Boolean),
          };
          const response = await eopdAPI.saveOpdStep(2, payload);
          if (response && response.success) {
            toast.success(response.message || "Chief Complaints saved successfully!");
            nextStep();
          } else {
            toast.error(response.message || "Failed to save chief complaints.");
          }
        } catch (error) {
          console.error(error);
          const errMsg = error.response?.data?.message || error.message || "Failed to save chief complaints.";
          toast.error(errMsg);
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => {
        const complaints = getIn(values, FIELDS.complaints) || [];
        const points = getIn(values, FIELDS.history) || [];

        const hasOther = hasOthersSelected(complaints);
        const currentMax = hasOther ? MAX_ITEMS_WITH_OTHERS : MAX_ITEMS;
        const isMaxReached = complaints.length >= MAX_ITEMS_WITH_OTHERS;

        const complaintsErr = getIn(errors, FIELDS.complaints);
        const complaintsTouched = getIn(touched, FIELDS.complaints);
        const showComplaintsErr =
          complaintsTouched && typeof complaintsErr === "string" ? complaintsErr : "";

        const historyErr = getIn(errors, FIELDS.history);
        const historyTouched = getIn(touched, FIELDS.history);
        const showHistoryErr =
          historyTouched && typeof historyErr === "string" ? historyErr : "";

        return (
          <Form noValidate>
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={isSubmitting}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            <Typography className="opd-step-title">
              Chief Complaints &amp; Brief History
            </Typography>

            {/* 1. Chief Complaints Accordion */}
            <Accordion
              expanded={expanded.complaints}
              onChange={handleAccordionChange("complaints")}
              className={`opd-accordion ${expanded.complaints ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">
                    Chief Complaints
                    <Box component="span" sx={{ color: "#EF4444", ml: 0.5, fontSize: "18px" }}>*</Box>
                  </Typography>
                  <Typography className="opd-accordion-subtitle">
                    Type at least 3 characters to search and select chief complaints. Minimum {MIN_ITEMS}, maximum {hasOther ? MAX_ITEMS_WITH_OTHERS : MAX_ITEMS} (up to 4 with Others).
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                <Box className="opd-input-group">
                  <Autocomplete
                    multiple
                    disabled={isSubmitting}
                    options={options}
                    loading={loadingOptions}
                    value={complaints}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    groupBy={(option) => option.categoryName || "Others"}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option.displayName || "";
                    }}
                    isOptionEqualToValue={(option, val) =>
                      (option.symptomId ?? option.symptom_id) === (val.symptomId ?? val.symptom_id)
                    }
                    getOptionDisabled={(option) => {
                      // If already selected, do not disable (allows deselecting)
                      if (
                        complaints.some(
                          (c) =>
                            (c?.symptomId ?? c?.symptom_id) ===
                            (option?.symptomId ?? option?.symptom_id)
                        )
                      ) {
                        return false;
                      }

                      // If 4 items are already selected, disable all
                      if (complaints.length >= MAX_ITEMS_WITH_OTHERS) {
                        return true;
                      }

                      const isOptionOther = isOtherComplaint(option);
                      // If 3 items are selected and none is "Others":
                      // Only "Others" can be selected as the 4th item
                      if (complaints.length >= MAX_ITEMS && !hasOther && !isOptionOther) {
                        return true;
                      }

                      return false;
                    }}
                    onBlur={() => setFieldTouched(FIELDS.complaints, true)}
                    onChange={(_, val) => {
                      const hasOthersInVal = hasOthersSelected(val);
                      const maxLimit = hasOthersInVal ? MAX_ITEMS_WITH_OTHERS : MAX_ITEMS;
                      const clean = val.slice(0, maxLimit);
                      setFieldValue(FIELDS.complaints, clean);
                      setFieldTouched(FIELDS.complaints, true, false);
                      setSelectedCount(clean.length);
                      setHasOtherSelected(hasOthersSelected(clean));

                      const stillHasOthers = hasOthersSelected(clean);
                      if (!stillHasOthers) {
                        setFieldValue(FIELDS.otherSymptoms, "");
                      }
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const isOther = isOtherComplaint(option);
                        const label = typeof option === "string"
                          ? option
                          : isOther
                            ? (option.displayName || "Others")
                            : option.categoryName && option.categoryName.trim()
                              ? `${option.displayName} (${option.categoryName})`
                              : option.displayName;
                        return (
                          <Chip
                            key={key}
                            {...tagProps}
                            label={label}
                            size="small"
                            className="opd-chip-selected"
                            sx={{
                              backgroundColor: "#1E88E5",
                              color: "#fff",
                              fontWeight: 600,
                              borderRadius: "8px",
                              mr: 0.5,
                              mb: 0.5,
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          readOnly: isMaxReached,
                        }}
                        placeholder={
                          isMaxReached
                            ? ""
                            : complaints.length === MAX_ITEMS && !hasOther
                              ? "Type to search 'Others'..."
                              : "Type to search complaints..."
                        }
                        error={Boolean(showComplaintsErr)}
                        helperText={
                          showComplaintsErr ||
                          (isMaxReached
                            ? `Maximum ${MAX_ITEMS_WITH_OTHERS} complaints selected (including Others)`
                            : complaints.length === MAX_ITEMS && !hasOther
                              ? "Maximum 3 complaints selected. You can search and add 'Others' as a 4th complaint."
                              : complaints.length === MAX_ITEMS && hasOther
                                ? "3 complaints selected. You can add 1 more complaint (up to 4 with Others)."
                                : "Type at least 3 characters to search complaints")
                        }
                        className="opd-input-field"
                      />
                    )}
                  />
                </Box>
                {hasOther && (
                  <Box className="opd-input-group" sx={{ mt: 2 }}>
                    <Typography className="opd-field-label">
                      Specify Other Symptom <Box component="span" sx={{ color: "#EF4444", ml: 0.5 }}>*</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      disabled={isSubmitting}
                      name={FIELDS.otherSymptoms}
                      value={values[FIELDS.otherSymptoms] || ""}
                      onChange={(e) => setFieldValue(FIELDS.otherSymptoms, e.target.value)}
                      onBlur={() => setFieldTouched(FIELDS.otherSymptoms, true)}
                      placeholder="Please specify other symptom(s)"
                      error={Boolean(touched[FIELDS.otherSymptoms] && errors[FIELDS.otherSymptoms])}
                      helperText={touched[FIELDS.otherSymptoms] && errors[FIELDS.otherSymptoms]}
                      className="opd-input-field"
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>

            {/* 2. Brief History Accordion */}
            <Accordion
              expanded={expanded.history}
              onChange={handleAccordionChange("history")}
              className={`opd-accordion ${expanded.history ? "opd-accordion-expanded" : ""}`}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="opd-accordion-summary">
                <Box>
                  <Typography className="opd-accordion-title">
                    Brief History
                    <Box component="span" sx={{ color: "#EF4444", ml: 0.5, fontSize: "18px" }}>*</Box>
                  </Typography>
                  <Typography className="opd-accordion-subtitle">
                    Add history points. Minimum {MIN_ITEMS}, maximum {MAX_ITEMS}.
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="opd-accordion-details">
                <FieldArray name={FIELDS.history}>
                  {({ push, remove }) => (
                    <Stack spacing={1.75}>
                      {points.map((point, i) => {
                        const itemErr = Array.isArray(historyErr) ? historyErr[i] : undefined;
                        const itemTouched = Array.isArray(historyTouched)
                          ? historyTouched[i]
                          : historyTouched;
                        const hasErr = Boolean(itemTouched && itemErr);

                        return (
                          <Box key={`${FIELDS.history}-${i}`} className="opd-input-group">
                            <Typography className="opd-field-label">
                              History Point {i + 1}
                            </Typography>

                            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                              <TextField
                                id={`${FIELDS.history}-${i}`}
                                name={`${FIELDS.history}[${i}]`}
                                fullWidth
                                size="small"
                                disabled={isSubmitting}
                                value={point}
                                onChange={(e) =>
                                  setFieldValue(`${FIELDS.history}[${i}]`, e.target.value)
                                }
                                onBlur={() => setFieldTouched(`${FIELDS.history}[${i}]`, true)}
                                placeholder="e.g. History of asthma in childhood"
                                error={hasErr}
                                helperText={hasErr ? itemErr : undefined}
                                className="opd-input-field"
                              />
                              <IconButton
                                onClick={() => {
                                  if (points.length > MIN_ITEMS) remove(i);
                                  else setFieldValue(`${FIELDS.history}[${i}]`, "");
                                }}
                                disabled={isSubmitting}
                                aria-label={`Delete history point ${i + 1}`}
                                title={
                                  points.length > MIN_ITEMS
                                    ? "Remove this point"
                                    : "At least one point is required — this will clear it"
                                }
                                className="opd-delete-btn"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        );
                      })}

                      <Box>
                        <Button
                          onClick={() => push("")}
                          disabled={isSubmitting || points.length >= MAX_ITEMS}
                          startIcon={<AddIcon />}
                          variant="outlined"
                          className="opd-back-btn"
                          sx={{ px: 2.5 }}
                        >
                          Add History Point
                        </Button>
                        {points.length >= MAX_ITEMS && (
                          <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                            Maximum {MAX_ITEMS} history points allowed
                          </FormHelperText>
                        )}
                        {showHistoryErr && (
                          <FormHelperText error sx={{ ml: 1, mt: 0.5 }}>
                            {showHistoryErr}
                          </FormHelperText>
                        )}
                      </Box>
                    </Stack>
                  )}
                </FieldArray>
              </AccordionDetails>
            </Accordion>

            {showActions && (
              <Stack
                direction="row"
                justifyContent={prevStep ? "space-between" : "flex-end"}
                alignItems="center"
                sx={{ mt: 3 }}
              >
                {prevStep && (
                  <Button
                    type="button"
                    variant="outlined"
                    className="opd-back-btn"
                    onClick={prevStep}
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  disabled={isSubmitting}
                  className="opd-next-btn"
                >
                  {submitLabel}
                </Button>
              </Stack>
            )}
          </Form>
        );
      }}
    </Formik>
  );
}
